import { useState } from "react";
import {
  SUPER_ADMIN_AUDIT_ACTOR_KINDS,
  SUPER_ADMIN_AUDIT_CATEGORIES,
  type SuperAdminAuditActorKind,
  type SuperAdminAuditCategory,
  type SuperAdminAuditLogItem,
  type SuperAdminAuditLogListResponse,
} from "@multi-chat/shared";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "../../components/ui/badge/index.js";
import { Button } from "../../components/ui/button/index.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { SimpleSelect } from "../../components/ui/select/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import type { SuperAdminLoadState } from "./SuperAdminDashboardPage.js";
import { SuperAdminCursorPaginationBar } from "./SuperAdminCursorPaginationBar.js";
import { superAdminInputClassName } from "./super-admin-setting-rows.js";
import {
  auditActionLabel,
  auditEntityLabel,
  formatAuditDetailValue,
  formatAuditTime,
} from "./super-admin-audit-display.js";
import {
  contactsTableHeaderLabelClass,
  superAdminTableBodyCellClass,
  superAdminTableBodyRowClass,
  superAdminTableClass,
  superAdminTableHeaderCellClass,
  superAdminTableHeadRowClass,
  superAdminTableScrollClass,
  superAdminTableWorkspaceClass,
  type SuperAdminTablePageSize,
} from "./super-admin-table-presentation.js";

export interface SuperAdminLogFilters {
  category: SuperAdminAuditCategory | "all";
  actorKind: SuperAdminAuditActorKind | "all";
  /** `YYYY-MM-DD`, local day, inclusive. */
  from: string;
  /** `YYYY-MM-DD`, local day, inclusive. */
  to: string;
}

export const EMPTY_SUPER_ADMIN_LOG_FILTERS: SuperAdminLogFilters = {
  category: "all",
  actorKind: "all",
  from: "",
  to: "",
};

export function isInvalidLogDateRange(filters: SuperAdminLogFilters): boolean {
  return filters.from !== "" && filters.to !== "" && filters.from > filters.to;
}

export type SuperAdminLogScope = { kind: "organization"; id: number } | { kind: "user"; id: number };

const selectTrigger = "h-9 min-w-[11rem] text-[0.8125rem]";

function HeaderCell({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <th className={superAdminTableHeaderCellClass}>
      <span className={contactsTableHeaderLabelClass}>{children}</span>
    </th>
  );
}

function scopeLabel(scope: SuperAdminLogScope, items: readonly SuperAdminAuditLogItem[]): string {
  if (scope.kind === "organization") {
    const match = items.find((item) => item.organization?.id === scope.id);
    return match?.organization?.name ?? `#${scope.id}`;
  }
  const match = items.find((item) => item.actor.userId === scope.id && item.actor.displayName);
  return match?.actor.displayName ?? `#${scope.id}`;
}

export function SuperAdminAuditActorBadge({ kind }: { kind: SuperAdminAuditActorKind }): React.JSX.Element {
  const { t } = useTranslation();
  switch (kind) {
    case "PLATFORM_ADMIN":
      return <Badge variant="outline">{t("superAdmin.logs.actorKind.PLATFORM_ADMIN")}</Badge>;
    case "ORGANIZATION_MEMBER":
      return <Badge variant="default">{t("superAdmin.logs.actorKind.ORGANIZATION_MEMBER")}</Badge>;
    case "SYSTEM":
      return <Badge variant="default">{t("superAdmin.logs.actorKind.SYSTEM")}</Badge>;
    default: {
      const exhaustive: never = kind;
      return exhaustive;
    }
  }
}

function ActingAsLine({
  actingAs,
}: {
  actingAs: NonNullable<SuperAdminAuditLogItem["actingAs"]>;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="text-[0.75rem] text-muted" data-testid="super-admin-log-acting-as">
      {actingAs.displayName
        ? t("superAdmin.logs.actor.actingAs", { name: actingAs.displayName })
        : t("superAdmin.logs.actor.actingAsUnknown", { id: actingAs.organizationMemberId })}
    </span>
  );
}

function ActorCell({
  actor,
  actingAs,
}: {
  actor: SuperAdminAuditLogItem["actor"];
  actingAs: SuperAdminAuditLogItem["actingAs"];
}): React.JSX.Element {
  const { t } = useTranslation();
  const name =
    actor.displayName ??
    (actor.kind === "SYSTEM" ? t("superAdmin.logs.actor.system") : t("superAdmin.logs.actor.unknown"));
  return (
    <span className="grid gap-1">
      <span className="flex flex-wrap items-center gap-2">
        {actor.userId !== null ? (
          <Link to={`/super-admin/users/${actor.userId}`} className="font-medium text-foreground no-underline hover:underline">
            {name}
          </Link>
        ) : (
          <span className="font-medium">{name}</span>
        )}
        <SuperAdminAuditActorBadge kind={actor.kind} />
      </span>
      {actingAs ? <ActingAsLine actingAs={actingAs} /> : null}
      {actor.email ? (
        <span className="text-[0.75rem] text-muted">
          <LtrValue>{actor.email}</LtrValue>
        </span>
      ) : null}
      {actor.serviceKey ? (
        <span className="font-mono text-[0.75rem] text-muted">
          <LtrValue>{actor.serviceKey}</LtrValue>
        </span>
      ) : null}
    </span>
  );
}

function OrganizationCell({
  organization,
}: {
  organization: SuperAdminAuditLogItem["organization"];
}): React.JSX.Element {
  const { t } = useTranslation();
  if (!organization) {
    return <span className="text-muted">{t("superAdmin.logs.values.empty")}</span>;
  }
  return (
    <Link
      to={`/super-admin/organizations/${organization.id}`}
      className="text-foreground no-underline hover:underline"
    >
      {organization.name}
    </Link>
  );
}

function ResourceText({ entity }: { entity: SuperAdminAuditLogItem["entity"] }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span>
      {auditEntityLabel(t, entity.type)}
      {entity.id ? (
        <>
          {" "}
          <LtrValue>#{entity.id}</LtrValue>
        </>
      ) : null}
    </span>
  );
}

export function SuperAdminLogsView({
  state,
  filters,
  scope,
  pageSize,
  onFiltersChange,
  onClearScope,
  onPageSizeChange,
  onNextPage,
  onFirstPage,
  isFirstPage,
  onRetry,
}: {
  state: SuperAdminLoadState<SuperAdminAuditLogListResponse>;
  filters: SuperAdminLogFilters;
  scope: SuperAdminLogScope | null;
  pageSize: SuperAdminTablePageSize;
  onFiltersChange: (filters: SuperAdminLogFilters) => void;
  onClearScope: () => void;
  onPageSizeChange: (size: SuperAdminTablePageSize) => void;
  onNextPage: () => void;
  onFirstPage: () => void;
  isFirstPage: boolean;
  onRetry: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState<SuperAdminAuditLogItem | null>(null);
  const invalidRange = isInvalidLogDateRange(filters);
  const items = state.status === "ready" ? state.data.items : [];
  const filtersActive =
    filters.category !== "all" || filters.actorKind !== "all" || filters.from !== "" || filters.to !== "";

  return (
    <div className="grid gap-4" data-testid="super-admin-logs">
      <div>
        <h1 className="m-0 text-xl font-semibold text-foreground">{t("superAdmin.logs.title")}</h1>
        <p className="m-0 mt-1 text-sm text-muted">{t("superAdmin.logs.lead")}</p>
      </div>

      <div className="flex flex-wrap items-end gap-3" data-testid="super-admin-logs-filters">
        <div className="grid gap-1">
          <span className="text-[0.75rem] font-medium text-muted">{t("superAdmin.logs.filters.category")}</span>
          <SimpleSelect
            value={filters.category}
            onValueChange={(value) =>
              onFiltersChange({ ...filters, category: value as SuperAdminLogFilters["category"] })
            }
            ariaLabel={t("superAdmin.logs.filters.category")}
            triggerClassName={selectTrigger}
            options={[
              { value: "all", label: t("superAdmin.logs.filters.allCategories") },
              ...SUPER_ADMIN_AUDIT_CATEGORIES.map((category) => ({
                value: category,
                label: t(`superAdmin.logs.categories.${category}`),
              })),
            ]}
          />
        </div>
        <div className="grid gap-1">
          <span className="text-[0.75rem] font-medium text-muted">{t("superAdmin.logs.filters.actorKind")}</span>
          <SimpleSelect
            value={filters.actorKind}
            onValueChange={(value) =>
              onFiltersChange({ ...filters, actorKind: value as SuperAdminLogFilters["actorKind"] })
            }
            ariaLabel={t("superAdmin.logs.filters.actorKind")}
            triggerClassName={selectTrigger}
            options={[
              { value: "all", label: t("superAdmin.logs.filters.allActors") },
              ...SUPER_ADMIN_AUDIT_ACTOR_KINDS.map((kind) => ({
                value: kind,
                label: t(`superAdmin.logs.actorKind.${kind}`),
              })),
            ]}
          />
        </div>
        <label className="grid gap-1">
          <span className="text-[0.75rem] font-medium text-muted">{t("superAdmin.logs.filters.from")}</span>
          <input
            type="date"
            dir="ltr"
            value={filters.from}
            max={filters.to || undefined}
            onChange={(event) => onFiltersChange({ ...filters, from: event.target.value })}
            className={`${superAdminInputClassName} h-9 w-auto`}
            data-testid="super-admin-logs-from"
          />
        </label>
        <label className="grid gap-1">
          <span className="text-[0.75rem] font-medium text-muted">{t("superAdmin.logs.filters.to")}</span>
          <input
            type="date"
            dir="ltr"
            value={filters.to}
            min={filters.from || undefined}
            onChange={(event) => onFiltersChange({ ...filters, to: event.target.value })}
            className={`${superAdminInputClassName} h-9 w-auto`}
            data-testid="super-admin-logs-to"
          />
        </label>
        {filtersActive ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onFiltersChange(EMPTY_SUPER_ADMIN_LOG_FILTERS)}
            data-testid="super-admin-logs-clear-filters"
          >
            {t("superAdmin.logs.filters.clear")}
          </Button>
        ) : null}
      </div>

      {scope ? (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="super-admin-logs-scope">
          <Badge variant="outline">
            {scope.kind === "organization"
              ? t("superAdmin.logs.scope.organization", { name: scopeLabel(scope, items) })
              : t("superAdmin.logs.scope.user", { name: scopeLabel(scope, items) })}
          </Badge>
          <Button type="button" size="sm" variant="ghost" onClick={onClearScope} data-testid="super-admin-logs-clear-scope">
            {t("superAdmin.logs.scope.clear")}
          </Button>
        </div>
      ) : null}

      {invalidRange ? (
        <p className="m-0 text-sm text-destructive" role="alert" data-testid="super-admin-logs-invalid-range">
          {t("superAdmin.logs.filters.invalidRange")}
        </p>
      ) : null}

      {!invalidRange && state.status === "loading" ? (
        <div
          className={`${superAdminTableWorkspaceClass} h-40 animate-pulse`}
          data-testid="super-admin-logs-loading"
          aria-busy="true"
        />
      ) : null}

      {!invalidRange && state.status === "error" ? (
        <ProductErrorState
          testId="super-admin-logs-error"
          title={t("superAdmin.logs.loadErrorTitle")}
          description={t("superAdmin.logs.loadErrorBody")}
          onRetry={onRetry}
          retryLabel={t("superAdmin.retry")}
        />
      ) : null}

      {!invalidRange && state.status === "ready" && state.data.items.length === 0 ? (
        <p className="m-0 text-sm text-muted" data-testid="super-admin-logs-empty">
          {filtersActive || scope ? t("superAdmin.logs.emptyFiltered") : t("superAdmin.logs.empty")}
        </p>
      ) : null}

      {!invalidRange && state.status === "ready" && state.data.items.length > 0 ? (
        <div className={superAdminTableWorkspaceClass}>
          <div className={superAdminTableScrollClass}>
            <table className={`${superAdminTableClass} min-w-[52rem]`} data-testid="super-admin-logs-table">
              <thead>
                <tr className={superAdminTableHeadRowClass}>
                  <HeaderCell>{t("superAdmin.logs.columns.time")}</HeaderCell>
                  <HeaderCell>{t("superAdmin.logs.columns.action")}</HeaderCell>
                  <HeaderCell>{t("superAdmin.logs.columns.actor")}</HeaderCell>
                  <HeaderCell>{t("superAdmin.logs.columns.organization")}</HeaderCell>
                  <HeaderCell>{t("superAdmin.logs.columns.resource")}</HeaderCell>
                </tr>
              </thead>
              <tbody>
                {state.data.items.map((item) => (
                  <tr
                    key={item.id}
                    className={superAdminTableBodyRowClass}
                    data-testid={`super-admin-log-row-${item.id}`}
                  >
                    <td className={`${superAdminTableBodyCellClass} whitespace-nowrap align-top text-muted`}>
                      <bdi>{formatAuditTime(item.occurredAt, i18n.language)}</bdi>
                    </td>
                    <td className={`${superAdminTableBodyCellClass} align-top`}>
                      <button
                        type="button"
                        className="cursor-pointer border-0 bg-transparent p-0 text-start font-medium text-foreground hover:underline focus-visible:underline"
                        onClick={() => setSelected(item)}
                        data-testid={`super-admin-log-open-${item.id}`}
                      >
                        {auditActionLabel(t, item.actionType)}
                      </button>
                    </td>
                    <td className={`${superAdminTableBodyCellClass} align-top`}>
                      <ActorCell actor={item.actor} actingAs={item.actingAs} />
                    </td>
                    <td className={`${superAdminTableBodyCellClass} align-top`}>
                      <OrganizationCell organization={item.organization} />
                    </td>
                    <td className={`${superAdminTableBodyCellClass} align-top text-muted`}>
                      <ResourceText entity={item.entity} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SuperAdminCursorPaginationBar
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            hasNextPage={state.data.nextCursor !== null}
            isFirstPage={isFirstPage}
            onNextPage={onNextPage}
            onFirstPage={onFirstPage}
            pageSizeLabel={t("superAdmin.pagination.pageSize")}
            nextLabel={t("superAdmin.logs.older")}
            firstLabel={t("superAdmin.logs.newest")}
            showingCountLabel={t("superAdmin.pagination.showingCount")}
            itemCount={state.data.items.length}
            testIdPrefix="super-admin-logs"
          />
        </div>
      ) : null}

      <SuperAdminLogDetailDialog item={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

export function SuperAdminLogDetailDialog({
  item,
  onClose,
}: {
  item: SuperAdminAuditLogItem | null;
  onClose: () => void;
}): React.JSX.Element {
  return (
    <AlertDialog
      open={item !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AlertDialogContent className="w-[min(92vw,36rem)]">
        {item ? <SuperAdminLogDetailBody item={item} onClose={onClose} /> : null}
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function SuperAdminLogDetailBody({
  item,
  onClose,
}: {
  item: SuperAdminAuditLogItem;
  onClose: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const row = "grid gap-0.5 sm:grid-cols-[9rem_1fr] sm:gap-3";
  const term = "text-[0.75rem] font-medium text-muted";
  return (
    <div className="grid gap-4" data-testid="super-admin-log-detail">
      <AlertDialogCloseButton label={t("common.close")} onClose={onClose} />
      <AlertDialogTitle className="pe-8">{auditActionLabel(t, item.actionType)}</AlertDialogTitle>
      <AlertDialogDescription>
        <bdi>{formatAuditTime(item.occurredAt, i18n.language)}</bdi>
      </AlertDialogDescription>
      <dl className="m-0 grid gap-3 text-[0.8125rem]">
        <div className={row}>
          <dt className={term}>{t("superAdmin.logs.columns.actor")}</dt>
          <dd className="m-0">
            <ActorCell actor={item.actor} actingAs={item.actingAs} />
          </dd>
        </div>
        <div className={row}>
          <dt className={term}>{t("superAdmin.logs.columns.organization")}</dt>
          <dd className="m-0">
            <OrganizationCell organization={item.organization} />
          </dd>
        </div>
        <div className={row}>
          <dt className={term}>{t("superAdmin.logs.columns.resource")}</dt>
          <dd className="m-0">
            <ResourceText entity={item.entity} />
          </dd>
        </div>
      </dl>

      <section className="grid gap-2">
        <h3 className="m-0 text-sm font-semibold text-foreground">{t("superAdmin.logs.detail.details")}</h3>
        {item.details.length === 0 ? (
          <p className="m-0 text-sm text-muted" data-testid="super-admin-log-detail-empty">
            {t("superAdmin.logs.detail.noDetails")}
          </p>
        ) : (
          <dl className="m-0 grid gap-2 rounded-lg border border-line bg-surface p-3 text-[0.8125rem]">
            {item.details.map((detail) => (
              <div key={detail.key} className={row} data-testid={`super-admin-log-detail-${detail.key}`}>
                <dt className={term}>{t(`superAdmin.logs.detailKeys.${detail.key}`)}</dt>
                <dd className="m-0 break-words">
                  <bdi>{formatAuditDetailValue(t, i18n.language, detail)}</bdi>
                </dd>
              </div>
            ))}
          </dl>
        )}
      </section>

      {item.ipAddress || item.userAgent ? (
        <section className="grid gap-2">
          <h3 className="m-0 text-sm font-semibold text-foreground">{t("superAdmin.logs.detail.request")}</h3>
          <dl className="m-0 grid gap-2 text-[0.8125rem]">
            {item.ipAddress ? (
              <div className={row}>
                <dt className={term}>{t("superAdmin.logs.detail.ipAddress")}</dt>
                <dd className="m-0">
                  <LtrValue>{item.ipAddress}</LtrValue>
                </dd>
              </div>
            ) : null}
            {item.userAgent ? (
              <div className={row}>
                <dt className={term}>{t("superAdmin.logs.detail.userAgent")}</dt>
                <dd className="m-0 break-words text-muted">
                  <LtrValue>{item.userAgent}</LtrValue>
                </dd>
              </div>
            ) : null}
          </dl>
        </section>
      ) : null}

      <AlertDialogActions>
        <Button type="button" size="sm" variant="outline" onClick={onClose}>
          {t("common.close")}
        </Button>
      </AlertDialogActions>
    </div>
  );
}
